import type { UserListItem } from "../features/users/types";
import type { User } from "./app";
import type { LeaveDetail, PathedInfoRequest } from "./aries";
import type { EmploymentType, ExemptionStatus, HoursCalculationStatus, UnionStatus, WorkLocationType } from "./leave-request.types";

/**
 * Represents the Case Status in numerical format.
 *
 * Maps to backend CaseStatus IntEnum */
export enum CaseStatus {
  Open = 1,
  Closed = 2,
  Cancelled = 3,
  Pending = 4,
  Submitted = 5,
  Accepted = 6,
  NotRequired = 7,
  Rejected = 8,
  /** Approved by the case owner (LOA-1462). Case details are locked; action items stay workable. */
  Approved = 9,
  /** Refused by the case owner, with a reason (LOA-1520). Read-only like Cancelled; can be reopened. */
  Denied = 10,
}

/**
 * Numerical time period mapping for Cases.
 *
 * Maps to backend LeavePeriod IntEnum */

export enum LeavePeriod {
  // eTimeUnit
  Hours = 1,
  Days = 2,
  Weeks = 3,
  Months = 4,
  Years = 5,
}

export interface CaseClient {
  id: string;
  name: string;
  is_active: boolean;
}

export enum PayBasis {
  Salary = "salary",
  Hourly = "hourly",
}

export enum FLSA {
  Contractor = "contractor",
  Exempt = "exempt",
  NonExempt = "non-exempt",
  NotApply = "na",
}

export enum CompanyCalendarType {
  Annual = "annual",
  FixedDate = "fixed-date",
  StartDate = "start-date",
  Rolling = "rolling",
}

/**
 * One entry in EmployeeContext.work_location[].
 * Matches BE's EmployeeWorkLocation model and the LOA-405 ADR sample.
 * location_type values ('primary_onsite' | 'hybrid_remote' | 'hybrid_onsite')
 * are server-side strings.
 */
export interface WorkLocation {
  id?: string;
  location_type?: string;
  /**
   * Whether the employee's company-location link is the primary worksite
   * (LOA-1239). Carried because that link has no `location_type`.
   */
  is_primary?: boolean;
  city?: string;
  state?: string;
  zipcode?: string;
  percentage?: number;
  is_active?: boolean;
  created_at?: string;
}

/**
 * One `employee_locations[]` entry — a link between an employee and a company
 * `Location` (backend `EmployeeLocation`).
 *
 * @remarks
 * `location` is null when the link deliberately carries no worksite (a remote
 * employee's residence, or an unknown location). At most one link per employee
 * may be primary; zero primaries is valid.
 */
export interface EmployeeLocationLink {
  id: string;
  location: EmployeeLocationSite | null;
  is_primary: boolean;
  is_active?: boolean;
  created_at?: string;
}

/**
 * The expanded `Location` nested inside {@link EmployeeLocationLink}.
 * Mirrors `LocationSerializer` on the backend.
 */
export interface EmployeeLocationSite {
  id: string;
  /** Company Details Location Name; absent on worksites saved before LOA-1110. */
  name?: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  country: string | null;
  is_hq?: boolean;
  timezone?: string | null;
}

/**
 * `EmployeeContext.reporting_location` — where the employee reports, resolved
 * backend-side: the employee's primary work location, or, when remote or when
 * no primary location is on file, the company `Location` reached by walking
 * the manager chain to a designated HQ (LOA-1008, LOA-1309).
 *
 * @remarks
 * `id` is present only for a company worksite; never assume the value matches
 * an option in the `locations` keyword list. Null for any employee whose
 * source resolved to nothing — no primary location and no HQ reachable via
 * the chain walk.
 */
export interface ReportingLocation {
  id?: string;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country?: string | null;
  is_hq?: boolean;
}

/**
 * `EmployeeContext.workplace_location` — the employee's single canonical
 * workplace location: the primary worksite when onsite, or the home
 * address when remote, resolved backend-side. Distinct from
 * {@link ReportingLocation}, which is the manager-chain-resolved location an
 * employee counts towards, not where they physically work. `source`
 * distinguishes how the value was resolved (e.g. "primary_location",
 * "home_address", "asked"). Absent when neither can be derived.
 *
 * Defined locally (not imported from `types/aries`) to match how the
 * sibling {@link ReportingLocation} type is defined in this file.
 */
export interface WorkplaceLocationContext {
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  source?: string | null;
}

/**
 * One leave history entry on EmployeeContext.leave_history[].
 * Matches BE's EmployeeLeaveHistory model.
 */
export interface LeaveHistoryEntry {
  id?: string;
  leave_type?: string;
  start_date?: string;
  /** Null while the leave is ongoing — nullable on the BE model. */
  end_date?: string | null;
  duration?: number;
  balance_used?: number;
  balance_remaining?: number;
  twelve_month_counter?: number;
  notes?: string;
  is_active?: boolean;
  created_at?: string;
}

/**
 * EmployeeContext.benefits — booleans + leave-time numerics. Matches BE's EmployeeBenefits model.
 */
export interface EmployeeBenefits {
  /**
   * False when the record has been soft-deleted. The employee detail
   * endpoint serialises the reverse relation unfiltered, so a retired
   * record still arrives here; the `/benefits/` sub-resource filters it
   * out. Absent on the `/context/` payload, which omits the flag.
   */
  is_active?: boolean;
  // Nullable, like the model: these are `BooleanField(null=True)`, and the
  // importer leaves a blank column NULL. Null means "never stated", which
  // is not the same as `false` ("explicitly not enrolled") — collapsing the
  // two is the defect this ticket exists to fix, on the flags as much as on
  // the balances below.
  has_medical?: boolean | null;
  has_std?: boolean | null;
  has_ltd?: boolean | null;
  // Nullable: the backend omits an unknown balance entirely
  // (`exclude_none`), and null/undefined must render as unknown, not 0.
  pto_available?: number | null;
  sick_time_available?: number | null;
}

/**
 * `Employee.manager` — the employee's manager, expanded by the backend from the
 * `manager_employee_id` reference it stores.
 *
 * @remarks
 * Null when no manager is recorded, and also when the recorded id names an
 * employee who is not on file (a first import often lists a report before their
 * manager), since the backend resolves the reference the same way the
 * manager-chain calculation does.
 */
export interface EmployeeManager {
  id: string;
  /** Business Employee ID — what gets written back on save. */
  employee_id: string;
  first_name: string | null;
  last_name: string | null;
  full_name?: string | null;
  job_title?: string | null;
}

/**
 * context.employee — direct mapping of BE's EmployeeContextNamespaceSerializer.
 * All keys optional; null/empty fields are omitted by the server per LOA-405.
 */
export interface EmployeeContext {
  employee_id?: string;
  first_name?: string;
  last_name?: string;
  work_email?: string;
  work_phone?: string;
  job_title?: string;
  department?: string;
  /** Residence (LOA-1235); the backend omits each field when blank. */
  home_street?: string | null;
  home_city?: string | null;
  home_state?: string | null;
  home_zipcode?: string | null;
  home_country?: string | null;
  work_location_type?: WorkLocationType;
  employment_type?: EmploymentType;
  exemption_status?: ExemptionStatus;
  union_status?: UnionStatus;
  hire_date?: string;
  termination_date?: string;
  adjusted_service_date?: string;
  hours_of_service?: number;
  hours_of_service_12_months?: number;
  months_of_service?: number;
  length_of_service?: number;
  hours_calculation_status?: HoursCalculationStatus;
  projected_hours_at_leave?: number | null;
  projected_earnings_at_leave?: number | null;
  work_location?: WorkLocation[];
  reporting_location?: ReportingLocation | null;
  workplace_location?: WorkplaceLocationContext | null;
  /**
   * Which laws apply, resolved server-side (rules-engine input only —
   * no UI reads these directly today). Must still round-trip through
   * `state.leaveDetail` unchanged: a case-scoped worksite-primary
   * recompute (LOA-1312) can update these without any other field
   * changing, and a later, unrelated drawer save that doesn't pick up
   * that update from the PATCH response would otherwise resubmit the
   * stale value and silently overwrite the recomputed one.
   */
  jurisdiction?: string | null;
  jurisdictions?: string[];
  jurisdiction_ancestors?: string[];
  /**
   * One ancestor-code list per direct parent, only populated when the
   * resolved jurisdiction has 2+ direct parents — see `jurisdiction`'s
   * comment for why this must round-trip through `state.leaveDetail`
   * unchanged (LOA-1312).
   */
  jurisdiction_parent_ancestor_sets?: string[][];
  /**
   * Employees within 75 miles of the case's reporting location (FMLA's
   * worksite test). The /intake agent derives it and marks it with
   * `worksite_employee_count_75_miles_source: "derived"`; the backend
   * recounts a marked value when the reporting location moves, so a drawer
   * save must resubmit the PATCH response's value, never a stale copy.
   */
  worksite_employee_count_75_miles?: number | null;
  worksite_employee_count_75_miles_source?: string | null;
  benefits?: EmployeeBenefits;
  leave_history?: LeaveHistoryEntry[];
}

/**
 * `context.workplaces[role]` — one workplace facility, keyed by the role it
 * plays for the employee (`most_time` is the only key the backend writes today).
 */
export interface WorkplaceContext {
  /** Present on the detail endpoint's workplace object; absent from /context/. */
  id?: string;
  title: string;
  zipcode?: string;
  employee_count?: number;
  /**
   * Company-wide headcount and this worksite's own headcount — the
   * canonical fields the Company Details editor writes back onto this
   * same `WorkplaceContext` shape. The `/context/` endpoint's Workplace
   * branch emits both; `exclude_none` on the backend means either is
   * absent (not `null`) when unset, including on the reporting-location
   * fallback branch, which doesn't populate them at all. On the read
   * side, `employeeToCompanyDetails` only consults `worksite_employee_count`
   * from this shape (at both the chat/T3 and persisted/T1 levels) — the
   * persisted (`emp.workplace`) copy of `total_employee_count` is never
   * read; the company-wide headcount instead falls back to
   * `emp.company?.headcount` (`Client.total_employee_count`), not this
   * field, once there's no chat-collected `total_employee_count`.
   */
  total_employee_count?: number;
  worksite_employee_count?: number;
  is_public_sector?: boolean;
}

/**
 * context.company — Client model fields plus flattened Client.extra_data keys.
 * `calendar_method` is a free-form string today (e.g. 'rolling_12m'), not CompanyCalendarType.
 */
export interface CompanyContext {
  name: string;
  subdomain: string;
  zipcode?: string;
  calendar_method?: string;
  roll_date?: string;
  headcount?: number;
}

/**
 * Wire response for GET /api/v1/employees/{id}/context/ (LOA-405).
 *
 * `workplaces` is a dict keyed by workplace role — not a list — and is omitted
 * entirely under the endpoint's default `sparse=true` when the employee has no
 * Workplace at all (an employee whose location is carried by `EmployeeLocation`
 * has none). Hence optional: see `serialise_leave_detail`'s `exclude_none` in
 * `api/v1/organizations/context_builder.py`. Mirrors `LeaveContext.workplaces`
 * in `types/aries`; read it via `PRIMARY_WORKPLACE_KEY`, never by index (LOA-1102).
 */
export interface EmployeeContextResponse {
  context: {
    employee: EmployeeContext;
    workplaces?: Record<string, WorkplaceContext> | null;
    company: CompanyContext;
  };
}

/**
 * UI view-model aligned with EmployeeDetailSerializer / EmployeeListSerializer (LOA-693).
 * Extends EmployeeContext (context endpoint fields) and adds detail-endpoint fields.
 * `work_locations` (plural) matches the backend serializer key; `work_location`
 * (singular) is inherited from EmployeeContext for the /context/ endpoint.
 */
export interface Employee extends EmployeeContext {
  id: string;
  first_name: string;
  last_name: string;
  // Computed / read-only fields from EmployeeCreateSerializer
  full_name?: string;
  /**
   * The employee's personal email. The backend does not send one yet (the
   * Employee model has only `work_email`); the case page reads it when it
   * does, and shows "Unknown" until then.
   */
  personal_email?: string | null;
  // Additional detail fields
  business_unit?: string;
  is_active?: boolean;
  // Derived status from EmployeeListSerializer ("active" | "terminated")
  status?: "active" | "terminated";
  // Plural key matches backend EmployeeCreateSerializer.work_locations
  work_locations?: WorkLocation[];
  /** Normalized links to company Locations (LOA-973). */
  employee_locations?: EmployeeLocationLink[];
  created_at?: string;
  updated_date?: string;
  actor_created_by?: string | null;
  actor_updated_by?: string | null;
  /**
   * The employee's worksite, read from `context.workplaces.most_time`
   * (see `features/employees/queries.ts`). It was also served as its
   * own serializer field until LOA-1407 retired the `Workplace` model;
   * the context is the only source now.
   */
  workplace: WorkplaceContext | null;
  company: CompanyContext | null;
  /**
   * Worksite shown on the employee roster, resolved by the list serializer
   * from the primary `EmployeeLocation`. Null for a remote employee, who has
   * no worksite — their reporting location is derived instead, work that is
   * deferred to LOA-968. Absent on endpoints other than the list.
   */
  primary_work_location?: string | null;
  /**
   * Manager / Supervisor, expanded read-side. Write the manager's `employee_id`
   * via `manager_employee_id` on the create/update payload.
   */
  manager?: EmployeeManager | null;
}

export interface CaseLeaveReason {
  id: string;
  name: string;
  description: string;
}

export interface Case extends Record<string, unknown> {
  id: string;
  case_identifier: string;
  case_number: number;
  client: CaseClient;
  created_by: User;
  employee: Employee;
  start_date: string | null;
  end_date: string | null;
  leave_period: LeavePeriod;
  leave_reason: CaseLeaveReason | null;
  assigned_user: User | null;
  status: CaseStatus;
  /** Who approved the case and when (LOA-1462); null unless it is approved. */
  approved_by?: UserListItem | null;
  approved_at?: string | null;
  /** Why the case was last denied (LOA-1520), kept after a reopen; null if it never was. */
  denial_reason?: string | null;
  /** That reason as people read it, e.g. `Insufficient Balance`. */
  denial_reason_label?: string | null;
  /** True when the Quick agent created the case (through an integration key); such cases open on the read-only page. */
  created_via_integration?: boolean;
  /**
   * One or two sentences about the case, written by the Quick agent when it creates or
   * revises the case. Read-only; absent for cases it did not create. Shown under the Summary heading.
   */
  summary?: string | null;
  is_active: boolean;
  version: number;
  /**
   * Optimistic-lock counter (LOA-1349), separate from `version` above
   * (a pre-existing, client-writable, unvalidated field this feature
   * deliberately doesn't touch). A real `IntegerField` server-side —
   * echo this value back verbatim as `expected_revision` on a write
   * that opts into the compare-and-swap check.
   */
  revision: number;
  created_at: string;
  updated_at: string;
  /** Backfilled server-side on GET (see backend `CaseViewSet.retrieve`). */
  trace_id?: string | null;

  /**
   * The refined leave dates, which `CaseSerializer` emits flat as well as
   * nested under `contextual_prompt`. Read both spellings — a payload may
   * carry either.
   */
  anticipated_start_date?: string | null;
  anticipated_return_date?: string | null;
  actual_start_date?: string | null;

  /**
   * Mirrors `ContextualPromptSerializer` — the refined leave details, flat
   * on the Case model but nested in the payload. Optional because list
   * responses and older cached payloads may omit it.
   */
  contextual_prompt?: {
    leave_reason: string | null;
    relationship_to_impacted_person: string | null;
    anticipated_start_date: string | null;
    anticipated_return_date: string | null;
    actual_start_date: string | null;
    continuity_type: string | null;
  };

  /**
   * Computed session stage (`CaseSerializer.get_session_stage`) —
   * `ESTABLISHING` (no employee yet), `BASE_INFO` (required fields
   * missing), `REFINING` (round-1 chat still asking follow-ups),
   * `ASSESSMENT_READY` (a ruleset is already assigned). Backend always
   * returns this; optional here only so existing mock fixtures predating
   * this field don't all need updating (see `mocks/handlers/loaCases.ts`).
   */
  session_stage?: "ESTABLISHING" | "BASE_INFO" | "REFINING" | "ASSESSMENT_READY";
  /**
   * Outstanding InfoRequests for the current `session_stage`
   * (`CaseSerializer.get_pending_info_requests`). For `BASE_INFO` this is
   * the *unrelated* `LeaveAssessmentChecker` completeness gap (see
   * `chat_info_requests` below for the real round-1 checkpoint); only for
   * `REFINING` does it mirror `chat_info_requests` directly. Backend always
   * returns this; optional here for the same reason as `session_stage`
   * above.
   */
  pending_info_requests?: PathedInfoRequest[];
  /**
   * Raw round-1 (Creation) checkpoint (`Case.chat_info_requests`) — the list
   * whose emptiness triggers the backend's `ready-for-review` event. Prefer
   * {@link Case.ready_for_review} for stage decisions: this raw list can be
   * left stale by Round 2 (see that field's note). Kept for diagnostics and
   * for rendering what the chat still has outstanding. Detail responses only
   * — omitted from `list`.
   */
  chat_info_requests?: PathedInfoRequest[] | null;
  /**
   * The `ready-for-review` sentinel: Round 1 (Creation) questions are
   * exhausted. Server-derived from an empty `chat_info_requests` plus a
   * resolved employee (the `ESTABLISHING` guard).
   *
   * Detail responses only — a `list` row deliberately omits it, since a stale
   * list row is exactly the kind of source that must not decide stage.
   */
  ready_for_review?: boolean;
  /**
   * The `ready-for-assessment` sentinel: Round 2 (Refinement) questions are
   * exhausted. Server-derived from `Case.refinement_criteria`, which is
   * written only on that turn.
   *
   * This is the **later** sentinel and outranks {@link Case.ready_for_review}
   * when they disagree — Round 2 can resolve fields Round 1 left open, which
   * is precisely how a finished Case used to read as Creation-incomplete
   * (LOA-1081). See `computeInitialStage`. Detail responses only.
   */
  ready_for_assessment?: boolean;
  /**
   * True when employee/company data (any source: HRIS/SFTP sync, file
   * import, employee record edit) or the case's own `leave_detail`
   * (drawer edit, chat extraction) changed after the last generated
   * assessment (LOA-1264). Server-derived — survives reload and
   * cross-session changes, unlike the in-session `isDirty` reducer flag.
   * `false` when no assessment has been generated yet.
   */
  data_changed_since_assessment?: boolean;
  /**
   * LeaveDetail checkpoint persisted after every round-1 chat turn
   * (LOA-967). Seeds the assessment kickoff's context on pages with no live
   * chat state of their own (e.g. the standalone case page).
   */
  leave_detail?: LeaveDetail | null;
  /**
   * When `leave_detail` last changed server-side (drawer edit, chat/ARIES
   * extraction, or a generate/regenerate's own stamp) — `CaseSerializer`
   * exposes this read-only (LOA-1349) so the client has a compare-and-swap
   * token to echo back on `POST /cases/{id}/discard-edits/`. `null` for a
   * case whose `leave_detail` has never been written.
   */
  leave_detail_updated_at?: string | null;

  // TODO: Review these legacy fields and document their deprecation/removal plan
  // or keep them explicitly for backward compatibility if they are still required.
  leaveType: string;
  leaveDuration: number;
  intermittent: boolean;
  notes: string;
  requestDate: string;
  rulePackVersion: string;
  ownerId: string;
  traceId: string;
  contextualPrompt?: string;
}

/** A single option entry used by Select, ComboBox, and Radio components. */
export interface Keyword {
  /** Stable machine key, e.g. "new-york" */
  id: string;
  /** Display text, e.g. "New York, NY" */
  label: string;
  /** Stored value — typically the human-readable string */
  value: string;
  /**
   * Customer-facing statement of the need behind the option (what the leave
   * is for, e.g. "Bonding with a New Child"), from the keywords API's
   * `metadata.need_purpose`. Only the `qualifyingReason` category carries it
   * (LOA-1205); consumers fall back to `label` when absent.
   */
  needPurpose?: string;
}

/**
 * Response shape for GET /keywords.
 * Keys are category names (e.g. "locations"), values are option arrays.
 */
export type KeywordsMap = Record<string, Keyword[]>;

/** A single option within a {@link KeywordSpace}, matching the real `GET /api/v1/keywords/` wire shape. */
export interface KeywordOption {
  id: string;
  label: string;
  value: string;
  metadata: Record<string, unknown>;
}

/** A named, versioned enumeration of options, as returned by `GET /api/v1/keywords/`. */
export interface KeywordSpace {
  key: string;
  version: string;
  metadata: Record<string, unknown>;
  options: KeywordOption[];
}

/**
 * Field-name manifests for {@link KeywordOption}/{@link KeywordSpace}, diffed
 * against the committed OpenAPI schema by `keywordSpaceContract.test.ts` so
 * this hand-written mirror can't silently drift from the real backend shape.
 */
export const KEYWORD_OPTION_FIELDS = ["id", "value", "label", "metadata"] as const;
export const KEYWORD_SPACE_FIELDS = ["key", "version", "metadata", "options"] as const;

type KeywordOptionFieldsExhaustive =
  Exclude<keyof KeywordOption, (typeof KEYWORD_OPTION_FIELDS)[number]> extends never ? true : never;
export const KEYWORD_OPTION_FIELDS_EXHAUSTIVE: KeywordOptionFieldsExhaustive = true;

type KeywordSpaceFieldsExhaustive =
  Exclude<keyof KeywordSpace, (typeof KEYWORD_SPACE_FIELDS)[number]> extends never ? true : never;
export const KEYWORD_SPACE_FIELDS_EXHAUSTIVE: KeywordSpaceFieldsExhaustive = true;
